// Multipart upload with progress. fetch() can't report upload progress, so
// this uses XMLHttpRequest; auth and the one-shot 401 refresh mirror api().

import { getSessionToken, clearSession } from './session';
import { requestSessionRefresh } from './session-refresh';

export interface UploadResult<T> {
  status: number;
  body: T;
}

export class UploadError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`Upload failed (${status})`);
    this.name = 'UploadError';
  }
}

function send<T>(
  url: string,
  form: FormData,
  token: string | null,
  onProgress?: (fraction: number) => void,
): Promise<UploadResult<T>> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      let body: unknown = xhr.responseText;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        // non-JSON body stays text
      }
      resolve({ status: xhr.status, body: body as T });
    };
    xhr.onerror = () => reject(new UploadError(0, 'network_error'));
    xhr.send(form);
  });
}

export async function uploadFile<T>(
  url: string,
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<T> {
  const form = new FormData();
  form.append('file', file, file.name);
  let res = await send<T>(url, form, getSessionToken(), onProgress);
  if (res.status === 401) {
    const fresh = await requestSessionRefresh();
    if (fresh) res = await send<T>(url, form, fresh, onProgress);
  }
  if (res.status < 200 || res.status >= 300) {
    if (res.status === 401) clearSession();
    throw new UploadError(res.status, res.body);
  }
  onProgress?.(1);
  return res.body;
}
